(() => {
  const root = window.Shopify?.routes?.root || '/';

  let guardPromise = null;
  let giftGuardTimer = null;
  let lastCart = null;

  /*
   * A free gift is a line added by the gift picker (_gift = true)
   * or any product priced $0 (the free gift products).
   */
  function isPickedGift(item) {
    return String(item.properties?._gift || '').toLowerCase() === 'true';
  }

  function isGift(item) {
    return isPickedGift(item) || Number(item.original_price) === 0;
  }

  async function getCart() {
    const response = await fetch(`${root}cart.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Unable to read cart');
    }

    const cart = await response.json();
    lastCart = cart;
    return cart;
  }

  async function changeLine(key, quantity) {
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity
      })
    });

    if (!response.ok) {
      throw new Error('Unable to update cart');
    }

    return response.json();
  }

  function refreshDrawer() {
    if (
      window.MinimogEvents &&
      window.MinimogTheme &&
      window.MinimogTheme.pubSubEvents
    ) {
      window.MinimogEvents.emit(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        { source: 'gift-guard' }
      );
    }

    const drawer = document.querySelector('m-cart-drawer');

    if (drawer && drawer.onCartDrawerUpdate) {
      drawer.onCartDrawerUpdate(true);
    }
  }

  async function runGiftGuard() {
    const cart = await getCart();

    const giftItems = cart.items.filter(isGift);
    const paidItems = cart.items.filter(item => !isGift(item));

    /*
     * IMPORTANT:
     * If customer removed every paid product,
     * remove all free gifts too.
     */
    if (paidItems.length === 0) {
      for (const gift of giftItems) {
        await changeLine(gift.key, 0);
      }

      if (giftItems.length) {
        await getCart();
        refreshDrawer();
      }

      return;
    }

    /*
     * Customer has paid products but
     * has not selected a free gift.
     */
    if (!giftItems.length) {
      return;
    }

    // Keep only one free gift (the one picked in the gift picker if there is one).
    const giftToKeep = giftItems.find(isPickedGift) || giftItems[0];

    let cartChanged = false;

    // Force gift quantity to exactly 1.
    if (giftToKeep.quantity !== 1) {
      await changeLine(giftToKeep.key, 1);
      cartChanged = true;
    }

    // Remove every other gift line.
    for (const gift of giftItems) {
      if (gift.key !== giftToKeep.key) {
        await changeLine(gift.key, 0);
        cartChanged = true;
      }
    }

    if (cartChanged) {
      await getCart();
      refreshDrawer();
    }
  }

  /*
   * Callers that are already waiting on a running check
   * get the same promise, so checkout always waits for it.
   */
  window.ivevarEnforceSingleGift = function () {
    if (guardPromise) return guardPromise;

    guardPromise = runGiftGuard()
      .catch(error => {
        console.error('IVEVAR gift guard:', error);
      })
      .finally(() => {
        guardPromise = null;
      });

    return guardPromise;
  };

  function scheduleGiftGuard(delay = 500) {
    clearTimeout(giftGuardTimer);

    giftGuardTimer = setTimeout(() => {
      window.ivevarEnforceSingleGift();
    }, delay);
  }

  function stop(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function hasPaidItems(cart) {
    return !!cart && cart.items.some(item => !isGift(item));
  }

  /*
   * Checkout from the cart page (and any other checkout link or button
   * outside the cart drawer): check the free gifts first, and never go
   * to checkout with only free items.
   */
  async function safeCheckout(form) {
    try {
      await window.ivevarEnforceSingleGift();
      const cart = await getCart();

      if (!hasPaidItems(cart)) {
        window.location.href = `${root}cart`;
        return;
      }

      if (form) {
        const flag = document.createElement('input');
        flag.type = 'hidden';
        flag.name = 'checkout';
        flag.value = '1';
        form.appendChild(flag);
        HTMLFormElement.prototype.submit.call(form);
        return;
      }

      window.location.href = `${root}checkout`;
    } catch (error) {
      console.error('IVEVAR safe checkout:', error);
      window.location.href = `${root}cart`;
    }
  }

  function isCheckoutLink(link) {
    try {
      const url = new URL(link.href, window.location.href);
      return url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname);
    } catch (e) {
      return false;
    }
  }

  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;

      // The cart drawer checkout button already has its own checks.
      if (e.target.closest('.scd__checkout')) return;

      const link = e.target.closest('a[href]');
      if (link && isCheckoutLink(link)) {
        stop(e);
        safeCheckout(null);
        return;
      }

      const button = e.target.closest('button[name="checkout"], input[name="checkout"]');
      if (button) {
        stop(e);
        safeCheckout(button.form);
        return;
      }

      // Express checkout buttons on the cart page (PayPal, Shop Pay...)
      const express = e.target.closest('.additional-checkout-buttons');
      if (express && lastCart && !hasPaidItems(lastCart)) {
        stop(e);
        window.ivevarEnforceSingleGift().then(() => {
          window.location.href = `${root}cart`;
        });
      }
    },
    true
  );

  /*
   * Check when page loads / customer comes back.
   */
  window.addEventListener('pageshow', () => {
    scheduleGiftGuard(300);
  });

  /*
   * IMPORTANT:
   * Check after customer removes or changes
   * items inside the cart drawer or on the cart page.
   */
  document.addEventListener('click', function (e) {
    const cartAction = e.target.closest(
      'm-cart-remove-button, .scd-item__remove, .m-cart-quantity--button, .change-gift-btn'
    );

    if (cartAction) {
      scheduleGiftGuard(800);
    }
  });

  // Quantity typed into the box on the cart page
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('[name="updates[]"], .m-cart-quantity--input')) {
      scheduleGiftGuard(1500);
    }
  });

  // Any cart update made by the theme (add to cart, cart page changes)
  function watchCartUpdates() {
    const events = window.MinimogEvents;
    const theme = window.MinimogTheme;

    if (!events || !theme || !theme.pubSubEvents) return false;

    events.subscribe(theme.pubSubEvents.cartUpdate, function (data) {
      if (data && data.source === 'gift-guard') return;
      scheduleGiftGuard(400);
    });

    return true;
  }

  /*
   * Extra protection whenever the cart drawer updates.
   */
  document.addEventListener('DOMContentLoaded', function () {
    const drawer = document.querySelector('m-cart-drawer');

    if (drawer) {
      const observer = new MutationObserver(function () {
        scheduleGiftGuard(500);
      });

      observer.observe(drawer, {
        childList: true,
        subtree: true
      });
    }

    if (!watchCartUpdates()) {
      window.addEventListener('load', watchCartUpdates);
    }

    scheduleGiftGuard(300);
  });

})();
